import React, { useRef, useState } from 'react';
import { ImagePlus, X, AlertTriangle, Check } from 'lucide-react';
import { uploadTextures, deleteTexture } from '../lib/api';
import { ActionBtn } from '../components/ReorderControls';

export const missingTextures = (it) => {
  const done = new Set((it.textures || []).map((t) => t.material));
  return (it.missing_textures || []).filter((m) => !done.has(m));
};

export function NoTexturesBadge({ it }) {
  const n = missingTextures(it).length;
  if (!n) return null;
  return (
    <span className="font-osd text-[10px] tracking-widest text-[#1a0e05] bg-[#ff8a3a] px-2 py-0.5 rounded-sm shadow-[0_0_10px_rgba(255,138,58,0.55)]" data-testid={`admin-no-textures-${it.id}`}>
      NO TEXTURES · {n}
    </span>
  );
}

export default function TexturePanel({ it, onSaved }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const missing = missingTextures(it);
  const added = it.textures || [];
  if (!missing.length && !added.length) return null;

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true); setErr(null);
    try { const res = await uploadTextures(it.id, files); setReport(res); onSaved(); }
    catch (x) { setErr(x?.response?.data?.detail || 'Upload failed'); }
    setBusy(false);
  };
  const remove = async (material) => {
    setErr(null); setReport(null);
    try { await deleteTexture(it.id, material); onSaved(); }
    catch (x) { setErr(x?.response?.data?.detail || 'Remove failed'); }
  };

  return (
    <div className="mt-3 p-3 border-2 border-[#7a3a14] rounded-lg bg-[#1a0a04] space-y-2" data-testid={`admin-textures-${it.id}`}>
      {missing.length > 0 && (
        <>
          <div className="flex items-start gap-2 font-osd text-[11px] text-[#ffb07a] tracking-wider">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>THESE PARTS HAVE NO TEXTURE IN THE FILE, SO THEY SHOW PLAIN WHITE: <span className="text-[#ffe9b8]" data-testid={`admin-missing-list-${it.id}`}>{missing.join(', ')}</span></span>
          </div>
          <p className="font-body text-xs text-[#a88552]" data-testid={`admin-texture-tip-${it.id}`}>
            Fix in Blender: File → External Data → Pack Resources, then export .glb again. Or add the images here, named after each part (e.g. <em>{missing[0]}.png</em>).
          </p>
        </>
      )}
      {added.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`admin-texture-list-${it.id}`}>
          {added.map((t) => (
            <span key={t.material} className="inline-flex items-center gap-1 font-osd text-[10px] text-[#9dff7a] border border-[#9dff7a]/40 rounded px-1.5 py-0.5">
              <Check className="w-3 h-3" />{t.material}
              <button type="button" onClick={() => remove(t.material)} aria-label={`Remove texture for ${t.material}`} className="ml-0.5 hover:text-[#ff6a3a]" data-testid={`admin-texture-remove-${it.id}-${t.material}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" multiple hidden onChange={pick} data-testid={`admin-texture-input-${it.id}`} />
      <ActionBtn onClick={() => inputRef.current?.click()} disabled={busy} testId={`admin-add-textures-${it.id}`} label={`Add textures for ${it.title}`}>
        <ImagePlus className="w-4 h-4" /> {busy ? 'UPLOADING…' : 'ADD TEXTURES'}
      </ActionBtn>
      {report && (
        <div className="font-osd text-[11px] tracking-wider space-y-0.5" data-testid={`admin-texture-report-${it.id}`}>
          {report.matched.map((m) => <div key={m.file} className="text-[#9dff7a]">✓ {m.file} → {m.material}</div>)}
          {report.unmatched.length > 0 && <div className="text-[#ff8a5c]" data-testid={`admin-texture-skipped-${it.id}`}>SKIPPED (NO MATCHING PART): {report.unmatched.join(', ')}</div>}
        </div>
      )}
      {err && <div className="font-osd text-xs text-[#ff6a3a]" role="alert">{err}</div>}
    </div>
  );
}
